// src/components/restake/types.ts

import { CoinOption } from "@kelp-dao/kernel-design-system/dist/components/text-fields/InputAmount";

export enum ETabs {
  RESTAKE = "restake",
  UNSTAKE = "unstake",
  WITHDRAW = "withdraw",
}

export enum EUnstakeMode {
  INSTANT = "instant",
  STANDARD = "standard",
}

export enum EWithdrawStatus {
  AVAILABLE = "available",
  PENDING = "pending",
  COMPLETED = "completed",
}

// Withdraw item status
export type TWithdrawStatus = EWithdrawStatus;

// Transaction mode type for unstake
export type TUnstakeMode = EUnstakeMode;

// Main component props
export type TRestakeUnstakeProps = {
  initialTab?: ETabs;
  title?: string;

  // Data props
  userBalance?: string;
  availableToUnstake?: string;
  coins?: CoinOption[];
  withdrawals?: TWithdrawItem[]; // Add withdraw data

  // Stats
  stats?: {
    tvl?: string;
    apy?: string;
  };

  // Assets restaked data
  assetsRestaked?: TAssetStat[];

  // Gain opportunities data
  gainOpportunities?: TGainOpportunity[];

  // Tab content data
  tabContent?: {
    restake?: TTabContentData;
    unstake?: TTabContentData;
    withdraw?: TTabContentData;
  };

  // Callback props
  onRestake?: (amount: string, coin: CoinOption | null) => Promise<void>;
  onUnstake?: (amount: string, coin: CoinOption | null) => Promise<void>;
  onWithdraw?: (amount: string, coin: CoinOption | null) => Promise<void>;
  onGainOpportunityClick?: (opportunity: TGainOpportunity) => void;

  // UI props
  isLoading?: boolean;

  // Customization
  className?: string;
  showAssetsRestaked?: boolean;
  showGainOpportunities?: boolean;
};

// Stats component props
export type TStatsCardProps = {
  stats: {
    label: string;
    value: string;
    variant?: "default" | "success" | "warning" | "error";
    justify?: "justify-start" | "justify-end";
  }[];
  className?: string;
  stakingApy?: number;
  restakingApy?: number;
  is7DayApy?: boolean;
  is90DayApy?: boolean;
};

// Asset stat type
export type TAssetStat = {
  value: string;
  coin: string;
  iconSrc: string;
};

// Gain opportunity type
export type TGainOpportunity = {
  id: string;
  title: string;
  apy: string;
  tvl: string;
  iconSrc: string;
  link: string;
  onClick?: () => void;
  metricLabel?: string;
};

// Gain opportunities props
export type TGainOpportunitiesProps = {
  opportunities: TGainOpportunity[];
  title?: string;
  className?: string;
};

// Tab content data
export type TTabContentData = {
  exchangeRate?: string;
  outputToken?: string;
  placeholder?: string;
  buttonText?: string;
};

// Enhanced tab form props to handle both restake and unstake
export type TRestakeFormProps = {
  isUnstakeTab?: boolean;
};

// Deprecated - keeping for backward compatibility
export type TUnstakeFormProps = TRestakeFormProps;

export type TTransactionData = {
  amount: string;
  coin: CoinOption | null;
  type: ETabs;
  mode?: TUnstakeMode; // Optional for backward compatibility
};

// Withdraw item type
export type TWithdrawItem = {
  id: string;
  amount: string;
  token: string;
  status: TWithdrawStatus;
  timestamp?: string; // For completed items
  onClaim?: () => void; // For available items
  isDisabled?: boolean; // For tracking disabled state
  beingWithdrawn?: boolean; // For tracking loading state during withdrawal
  waitingTime?: string; // For pending items to show waiting time
};

// Withdraw component props - simplified since we're getting data from hooks
export type TWithdrawProps = {
  className?: string;
};
